import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { buildProviders, Coerce, KalAutocompleteDirective, KalAutocompleteOption } from '@kalidea/kaligraphi';
import { Store } from '@ngxs/store';
import { finalize, map, take, tap } from 'rxjs/operators';
import {
    BenefComboModel,
    CriteresGetListeBenefForComboInput,
    MutationGetListeBenefModelsForComboArgs,
    ParametresUserAppliModelDto
} from 'src/graphql/generated-types';
import gql from 'graphql-tag';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserState } from 'src/app/_user/store/user/user.state';
import { CustomFormElement } from 'src/app/_core/tools/custom-form-element';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { BeneficiaryPipe } from 'src/app/_shared/pipes/beneficiary.pipe';
import { arrayEmpty } from 'src/app/utils/array.utils';

@Component({
    selector: 'app-beneficiary-picker',
    templateUrl: './beneficiary-picker.component.html',
    styleUrls: ['./beneficiary-picker.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(BeneficiaryPickerComponent),
    standalone: false
})
export class BeneficiaryPickerComponent extends CustomFormElement<any> implements OnInit, OnDestroy {

    control: FormControl<string | null>;

    beneficiariesList: KalAutocompleteOption<BenefComboModel>[] = [];

    outRightControl: FormControl<boolean>;

    @Input()
    filtersFunction: (item: KalAutocompleteOption<BenefComboModel>) => boolean = null;

    @Coerce('boolean')
    @Input()
    clearOnPick = false;

    @Input()
    label = '';

    @Coerce('boolean')
    @Input()
    kalSelectOnPick = true;

    @Coerce('boolean')
    @Input()
    displayOutRightCheckbox = false;

    @Coerce('boolean')
    @Input()
    selectOutRightCheckbox = false;

    @Input({transform: coerceBooleanProperty})
    showMatricules = false;

    @Output() selectedOnClick = new EventEmitter<KalAutocompleteOption<BenefComboModel>>();

    @ViewChild(KalAutocompleteDirective) kalAutoCompleteDirective: KalAutocompleteDirective;

    typedKeyword: string | null = null;

    constructor(private store: Store,
                private injector: Injector,
                public cdr: ChangeDetectorRef,
                private graphqlService: GraphqlService) {
        super();
    }

    private _selectedBeneficiary = new BehaviorSubject<BenefComboModel>(null);

    get selectedBeneficiary(): BenefComboModel {
        return this._selectedBeneficiary.getValue();
    }

    get selectedBeneficiary$(): Observable<BenefComboModel> {
        return this._selectedBeneficiary.asObservable();
    }

    private _loading = false;

    get loading(): boolean {
        return this._loading;
    }

    set loading(loading: boolean) {
        this._loading = loading;
        this.cdr.markForCheck();
    }

    get displayedList(): KalAutocompleteOption<BenefComboModel>[] {
        return this.beneficiariesList.filter(e => this.filtersFunction?.(e) ?? true)
            .filter(({value}) => {
                return !this.control.value || `${value?.Nom} ${value?.Prenom} ${value?.Matricule}`?.toLocaleLowerCase().includes(this.control.value?.toLocaleLowerCase());
            });
    }

    /** « Aucun résultat » uniquement après une recherche infructueuse (hors chargement). */
    get emptyMessage(): string {
        return (!!this.control?.value && !this.loading && arrayEmpty(this.displayedList)) ? 'Aucun résultat' : '';
    }

    private get applicationParameterList(): ParametresUserAppliModelDto[] {
        return this.store.selectSnapshot(UserState.applicationParameters);
    }

    private get isBigList(): boolean {
        return this.applicationParameterList.find(p => p.CodeChamp === 'BIGBNF')?.CodeValeur === '1';
    }

    private get defaultFilters(): CriteresGetListeBenefForComboInput {
        return {
            IsBigListe: this.isBigList,
            OnDelegationGlobale: this.store.selectSnapshot(UserState.currentDelegation).CEIdcGlob,
            WithCloture: false
        };
    }

    private get displayBlockedRights(): boolean {
        const displayBlockRightParameter = this.applicationParameterList.find(p => p.CodeChamp === 'BNFBDR');
        return !!displayBlockRightParameter && displayBlockRightParameter.CodeValeur === '1';
    }

    private get displayOutRights(): boolean {
        const displayOutRightsParameter = this.applicationParameterList.find(p => p.CodeChamp === 'BNFHSA');
        return !!displayOutRightsParameter && displayOutRightsParameter.CodeValeur === '1';
    }

    writeValue(value: number): void {
        if (value) {
            const filters: CriteresGetListeBenefForComboInput = {
                ...this.defaultFilters,
                IdBenef: value,
                IsBigListe: true
            };

            this.loading = true;

            this.watch$ = this.getBeneficiaries$(filters).pipe(
                take(1),
                tap(beneficiariesList => {
                    this._selectedBeneficiary.next(beneficiariesList?.[0] || null);

                    const value = !this.selectedBeneficiary
                        ? null
                        : BeneficiaryPipe.format(this.selectedBeneficiary.Prenom, this.selectedBeneficiary.Nom, this.showMatricules ? this.selectedBeneficiary.Matricule : null);

                    this.control.patchValue(value , {emitEvent: false});
                }),
                finalize(() => this.loading = false)
            );
        } else {
            this._selectedBeneficiary.next(null);
            this.control.patchValue(null, {emitEvent: false});
        }
    }

    pickBenef($event: KalAutocompleteOption<BenefComboModel>): void {
        if (!$event) {
            return;
        }

        this._selectedBeneficiary.next($event.value);
        this.notifyUpdate($event.value.Id);

        this.cdr.markForCheck();
    }

    pickBenefFromKeyEvent(): void {
        const foundBenef = this.beneficiariesList.find(b => [b.value.Matricule, b.value.Nom, b.value.Prenom].includes(this.typedKeyword)) || null;

        if(!foundBenef) {
            return;
        }

        this.pickBenef(foundBenef);
    }

    searchBeneficiary(nameFilter?: string): void {
        this.typedKeyword = nameFilter;

        const filters = this.defaultFilters;
        filters.PremiereLettre = nameFilter || '';

        if (this.isBigList && !nameFilter) {
            this.beneficiariesList = [];
            return;
        }

        if ((!this.isBigList || nameFilter.length === 1) && this.beneficiariesList.length === 0 && !this.loading) {

            this.loading = true;

            this.watch$ = this.getBeneficiaries$(filters).pipe(
                take(1),
                map(beneficiariesList => {
                    if (!this.isBigList || nameFilter) {

                        this.beneficiariesList = beneficiariesList.filter(({IsBlocageDroitActif, IsDroitsActifs}) => {
                            return (!IsBlocageDroitActif || this.displayBlockedRights)
                                && (IsDroitsActifs || (this.displayOutRightCheckbox ? this.outRightControl.value : this.displayOutRights));
                        })
                            .map(b => ({label: `${b.Nom} ${b.Prenom}`, matricule: b.Matricule, value: b}));

                    } else {
                        this.beneficiariesList = [];
                    }

                    return this.beneficiariesList;
                }),
                finalize(() => this.loading = false)
            );
        }
    }

    addBeneficiary(option): void {
        if (this.clearOnPick) {
            this.control.patchValue(null, {emitEvent: false});
        }

        this.selectedOnClick.emit(option);
    }

    clearBenef(): void {
        if (!this.selectedBeneficiary) {
            this.control.patchValue(null, {emitEvent: false});
        }
    }

    private getBeneficiaries$(filters: CriteresGetListeBenefForComboInput): Observable<BenefComboModel[]> {
        return this.graphqlService.mutateOne<'GetListeBenefModelsForCombo', MutationGetListeBenefModelsForComboArgs>({
            mutation: gql`
                mutation GetListeBenefModelsForCombo($contexte: ContextInfosInput, $critere: CriteresGetListeBenefForComboInput) {
                    GetListeBenefModelsForCombo(contexte: $contexte, critere: $critere) {
                        BenefComboModel {
                            Id
                            Nom
                            Prenom
                            Matricule
                            IsBlocageDroitActif
                            IsDroitsActifs
                            TypeCode
                        }
                    }
                }
            `,
            variables: {
                critere: filters
            }
        }).pipe(
            this.untilDestroyed,
            take(1),
            map(GetListeBenefModelsForCombo => GetListeBenefModelsForCombo?.BenefComboModel || []),
        );
    }

    ngOnInit(): void {
        this.outRightControl = new FormControl(this.selectOutRightCheckbox);

        this.watch$ = this.outRightControl.valueChanges.pipe(
            tap(() => this.beneficiariesList = [])
        );

        this.control = this.createControlAndSubscriptions(this.injector);

        this.watch$ = this.control.valueChanges
            .pipe(
                tap(value => {
                    if (this.selectedBeneficiary) {
                        this._selectedBeneficiary.next(null);
                        this.notifyUpdate(null);
                    }

                    this.searchBeneficiary(value);
                })
            );
    }

}
